import { isEqual, throttle } from 'lodash';
import { compose, escape } from 'lodash/fp';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import RetryButton from 'components/Practice/shared/RetryButton';
import UniversalPlayer from 'components/shared/UniversalPlayer';
import initVideoUploader from 'misc/wistia_upload';
import ZiggeoMediaPlayer from 'ui/ZiggeoVideo/ZiggeoMediaPlayer';
import { urlIsZiggeo } from 'utils/url';
import ElementLoading from 'components/shared/ElementLoading/ElementLoading.jsx';
import VideoDownloadButton from 'components/shared/UniversalDownloadButton/VideoDownloadButton.jsx';
import ElementBase from '../base.jsx';
import CaptionFooter from '../caption';
import RecordMenu from './RecordMenu';
import SubmitForm from './submit';

class EmbedElement extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      url: this.props.content.url,
      showRecordingMenu: false,
      updating: false,
      disabled: false,
      seismicVideoId: null,
      seismicVideoPlaybackLoaded: false,
    };
    this.type = this.props.content.embed_type;
    this.dropInId = `video_${this.props.id}`;
    // bindings
    this.setSize = throttle(this.setSize.bind(this), 100);
    this.updateUrl = this.updateUrl.bind(this);
    this.updateCaption = this.updateCaption.bind(this);
    this.submitEmbed = this.submitEmbed.bind(this);
    this.submitVideo = this.submitVideo.bind(this);
    this.applyChanges = this.applyChanges.bind(this);
    this.renderRecordMenu = this.renderRecordMenu.bind(this);
    this.toggleRecordMenu = this.toggleRecordMenu.bind(this);
    this.submitRecording = this.submitRecording.bind(this);
    this.submitSeismicRecording = this.submitSeismicRecording.bind(this);
    this.checkCanWisteriaInit = this.checkCanWisteriaInit.bind(this);
  }

  /**
   * Lifecycle
   */

  componentDidMount() {
    const canInitWistia = this.checkCanWisteriaInit();
    if (this.hasEmbed) {
      this.initResize();
    }
    if (canInitWistia) {
      this.initWistiaUploader();
    }
  }

  // eslint-disable-next-line
  componentDidUpdate({ content: prevContent }) {
    const { wide, url } = this.props.content;
    const didToggleWide = !isEqual(wide, prevContent.wide);
    const didTryAgain = !url && !!prevContent.url;
    const canInitWistia = this.checkCanWisteriaInit();
    if (canInitWistia && didTryAgain && !this.state.showRecordingMenu) {
      this.initWistiaUploader();
    }
    if (!this.hasEmbed) {
      return false;
    }
    if (didToggleWide) {
      setTimeout(() => this.initResize(), 300);
    } else {
      this.initResize();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setSize);
  }

  onPlayerEvent = (event = {}) => {
    if (event.stateType === 'playback' && event.state === 'loaded') {
      this.setState({ seismicVideoPlaybackLoaded: true });
    }
  };

  /**
   * Getters
   */

  get hasEmbed() {
    const { url, embed_code, metadata } = this.props.content;

    return (
      // eslint-disable-next-line camelcase
      (url && embed_code) ||
      (this.props.content.id && this.props.content.is_seismic_provider) ||
      metadata
    );
  }

  get isEmbedVideo() {
    return this.type === 'video' && this.props.content.embed_code;
  }

  get videoUrl() {
    const id = this.props.content.metadata.wistia_id;
    return `https://fast.wistia.net/embed/iframe/${id}`;
  }

  /**
   * Initializing
   */

  initResize() {
    this.setSize();
    window.addEventListener('resize', this.setSize);
  }

  initWistiaUploader() {
    const { id: projectId, token: accessToken } = this.props.tokens.wistia;
    initVideoUploader(accessToken, this.dropInId, projectId).then(
      this.submitVideo,
    );
  }

  /**
   * Resizing
   */

  setSize() {
    if (this.embedContainer && this.container) {
      const { width: containerWidth } = this.container.getBoundingClientRect();
      this.embedContainer.firstChild.width = containerWidth;
      this.embedContainer.firstChild.height = 0.56 * containerWidth;
    }
  }

  /**
   * Helpers
   */
  checkCanWisteriaInit() {
    const canInitWistia =
      !this.hasEmbed && !this.isEmbedVideo && this.props.features.uploadVideo;
    return canInitWistia;
  }

  /**
   * Actions
   */

  updateUrl(e) {
    this.setState({ url: e.target.value });
  }

  updateCaption(caption) {
    this.props.applyChanges({ caption: escape(caption) });
    this.props.handleUpdate({ caption: escape(caption) });
  }

  submitEmbed(e) {
    e.preventDefault();
    const { props, state, applyChanges } = this;
    props.handleUpdate({ url: state.url }).then(applyChanges);
  }

  submitVideo(metadata) {
    const { props, applyChanges } = this;
    props.handleUpdate({ metadata }).then(applyChanges);
  }

  submitRecording(url) {
    const { props, applyChanges } = this;
    this.setState({ url, updating: true }, () => {
      props.handleUpdate({ url }).then(applyChanges);
    });
  }

  submitSeismicRecording(data) {
    const isSeismic = true;
    const { props, applyChanges } = this;
    const { token } = data;
    if (token === null) this.setState({ disabled: true });
    else this.setState({ disabled: false });
    this.setState({ updating: true }, () => {
      if (token != null) {
        props
          .handleUpdate({
            isSeismic,
            token,
            file_format: data?.file_format || data?.format,
          })
          .then(applyChanges);
      } else {
        props.handleUpdate({ isSeismic, token }).then(applyChanges);
      }
    });
  }

  applyChanges(data) {
    if (data.is_seismic_provider === undefined) {
      data.is_seismic_provider = false;
    }
    this.setState({ updating: false });
    this.props.applyChanges({
      embed_code: data.embed_code,
      url: this.state.url,
      metadata: data.metadata,
      embedId: data.id,
      is_seismic_provider: data.is_seismic_provider,
    });
  }

  toggleRecordMenu() {
    const canInitWistia = this.checkCanWisteriaInit();
    this.setState(
      state => ({ showRecordingMenu: !state.showRecordingMenu }),
      () => {
        if (!this.state.showRecordingMenu && canInitWistia) {
          this.initWistiaUploader();
        }
      },
    );
  }

  setSeismicVideoId = thinContent => {
    let videoId = null;
    if (thinContent) {
      [, , videoId] = thinContent.split('__');
    }
    return this.setState({ seismicVideoId: videoId });
  };

  handleDelete = () => {
    this.setState({ seismicVideoPlaybackLoaded: false });
    this.submitSeismicRecording({ token: null });
  };

  /**
   * Rendering
   */

  renderEmbed() {
    const { content } = this.props;
    const ziggeoToken = urlIsZiggeo(this.state.url);

    return (
      <div className="d-f fd-c w-100p">
        {content.embed_code && (
          <div className="Video-redo ta-r p-8">
            <RetryButton onClick={() => this.submitRecording(null)} />
          </div>
        )}
        {ziggeoToken && (
          <ZiggeoMediaPlayer
            token={ziggeoToken}
            apiKey={this.props.ziggeoApiKey}
            className="maxh-600"
          />
        )}
        {!ziggeoToken && (
          <div
            ref={node => {
              this.embedContainer = node;
            }}
            dangerouslySetInnerHTML={{ __html: content.embed_code }}
          />
        )}
      </div>
    );
  }

  renderSeismicPlayer() {
    const embedId = this.props.content.id;
    const { isAdmin } = this.props.currentUser;
    const seismicVideoId =
      this.props.content.seismic_id || this.state.seismicVideoId;
    const { seismicVideoPlaybackLoaded } = this.state;

    return (
      <div className="d-f fd-c w-100p">
        <div className="Video-redo p-8">
          <div className="d-if f-r ta-l">
            {isAdmin && (
              <>
                <VideoDownloadButton
                  item={{
                    repository: 'Learning',
                    type: 'ExternalVideo',
                    id: seismicVideoId,
                  }}
                  isTryAgain
                  disabled={!seismicVideoPlaybackLoaded || !seismicVideoId}
                  currentUserId={this.props.currentUserId}
                />
              </>
            )}
            <RetryButton
              onClick={this.handleDelete}
              disabled={this.state.disabled}
            />
          </div>
        </div>
        <UniversalPlayer
          id={embedId}
          videoType="Embed"
          isPractice
          getThinContent={this.setSeismicVideoId}
          onPlayerEvent={this.onPlayerEvent}
        />
      </div>
    );
  }

  renderForm() {
    return (
      <SubmitForm
        embedId={this.props.id}
        dropInId={this.dropInId}
        onSubmit={this.submitEmbed}
        onUrlInput={this.updateUrl}
        isVideo={this.type === 'video'}
        canUploadVideo={this.props.features.uploadVideo}
        toggleRecordMenu={this.toggleRecordMenu}
        submitRecording={this.submitRecording}
        hasZiggeoForAllMedia={this.props.features.ziggeoForAllMedia}
        submitSeismicRecording={this.submitSeismicRecording}
      />
    );
  }

  renderVideo() {
    return (
      <div
        ref={node => {
          this.embedContainer = node;
        }}
      >
        <iframe
          title={t('lessons.editor.elements.embed')}
          src={this.videoUrl}
          frameBorder="0"
          width="640"
          height="360"
        />
      </div>
    );
  }

  renderRecordMenu() {
    return (
      <RecordMenu
        id={this.props.id}
        browserSupport={this.props.browserSupport}
        onSubmit={this.submitRecording}
        toggleRecordMenu={this.toggleRecordMenu}
        currentUserId={this.props.currentUserId}
      />
    );
  }

  renderLoadingSpinner() {
    const LOADING_TEXT = t('questions.types.content.loading');
    const { isAdmin } = this.props.currentUser;
    return (
      <div className="d-f fd-c w-100p">
        <div className="Video-redo p-8">
          <div className="d-if f-r ta-l">
            {isAdmin && <VideoDownloadButton item isTryAgain disabled />}
            <RetryButton />
          </div>
        </div>
        <ElementLoading isLoading text={LOADING_TEXT} />;
      </div>
    );
  }

  renderBody() {
    const { metadata, embed_code } = this.props.content;
    const { updating } = this.state;
    if (updating) return this.renderLoadingSpinner();
    if (metadata) return this.renderVideo();
    if (this.props.content.is_seismic_provider)
      return this.renderSeismicPlayer();
    if (embed_code) return this.renderEmbed(); // eslint-disable-line camelcase
    if (this.state.showRecordingMenu) return this.renderRecordMenu();
    return this.renderForm();
  }

  render() {
    return (
      <div className="EditorElement-content">
        <div className="EditorElement-body">
          <div
            ref={node => {
              this.container = node;
            }}
            className="EditorElement-fileHandler"
          >
            {this.renderBody()}
          </div>
        </div>
        <CaptionFooter
          onChange={this.updateCaption}
          caption={this.props.content.caption}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  browserSupport: state.meta.browserSupport,
  currentUserId: state.editor.currentUserId,
  ziggeoApiKey: state.editor.ziggeoApiKey,
});

export default compose(connect(mapStateToProps), ElementBase)(EmbedElement);
